import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  GlobalStyles,
  IconButton,
  Stack,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Papa from 'papaparse';
import { useRef } from 'react';
import React from 'react';
import * as XLSX from 'xlsx';

import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { Unit } from '@/Units/types';

import { ISubscibersBody } from '../types';

const useStyles = makeStyles({
  paper: {
    minHeight: 560,
    borderRadius: 10,
  },
});

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: Function;
  loading?: boolean;
  units: Unit[];
};

export const FormBulkSubscriber = ({
  open,
  onClose,
  onSubmit,
  loading: _loading = false,
  units,
}: Props) => {
  const classes = useStyles();
  const [isWrongData, setIsWrongData] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [fileType, setFileType] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const file = event.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase();

      if (extension === 'csv') {
        setFileType('CSV');
        handleCsvFile(file);
      } else if (extension === 'xlsx') {
        setFileType('XLSX');
        handleXlsxFile(file);
      } else {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();

        // Provide helpful error message for Numbers files
        if (extension === 'numbers') {
          setValidationErrors([
            'Mac Numbers files (.numbers) are not supported.',
            'Please export your Numbers file as CSV or Excel format:',
            '1. Open your file in Numbers',
            '2. Go to File → Export To → CSV (or Excel)',
            '3. Save the exported file',
            '4. Upload the CSV or Excel file here',
          ]);
        } else {
          setValidationErrors([
            `File type ".${extension}" is not supported.`,
            'Please upload a CSV (.csv) or Excel (.xlsx) file.',
          ]);
        }
      }
    }
  };

  const handleCsvFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        processData(results.data);
      },
      error: (_error) => {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      },
    });
  };

  const handleXlsxFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target) {
        const data = new Uint8Array(event.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length === 0) {
          console.error('The file is empty.');
          setIsLoading(false);
          resetFileInput();
          return;
        }

        const headers: string[] = jsonData[0] as string[];
        const parsedData = jsonData.slice(1).map((row: any[]) => {
          const rowData: any = {};
          headers.forEach((header: string, index: number) => {
            // Handle undefined/null values
            const cellValue = row[index];
            rowData[header] =
              cellValue === undefined || cellValue === null ? '' : String(cellValue);
          });
          return rowData;
        });

        processData(parsedData);
      } else {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleClose = () => {
    // Reset error state
    setIsWrongData(false);
    setValidationErrors([]);

    // Reset file type
    setFileType('');

    // Reset loading state
    setIsLoading(false);

    // Clear file input
    resetFileInput();

    // Call the original onClose prop
    onClose();
  };

  const processData = (jsonData: any[]) => {
    // Convert headers to lowercase and trim spaces
    const normalizeData = (data: any[]) => {
      return data.map((item) => {
        return Object.fromEntries(
          Object.entries(item).map(([key, value]) => [key.trim().toLowerCase(), value]),
        );
      });
    };

    // Normalize the incoming data
    jsonData = normalizeData(jsonData);

    // Filter data to remove any entry with an empty 'unit name'
    jsonData = jsonData.filter((item: any) => item['unit name']?.trim() !== '');

    if (jsonData.length === 0) {
      setIsWrongData(true);
      setIsLoading(false);
      resetFileInput();
      return;
    }

    // Phone number normalization and validation function
    const normalizeAndValidatePhoneNumber = (
      phoneNumber?: string | null,
    ): { isValid: boolean; normalized: string } => {
      if (!phoneNumber || phoneNumber.trim() === '') {
        return { isValid: true, normalized: '' }; // Optional field
      }

      // Clean up the phone number - remove all non-digit characters except +
      let cleaned = phoneNumber.replace(/[^\d+]/g, '');

      // If it doesn't start with +, assume it's a US number and add +1
      if (!cleaned.startsWith('+')) {
        // Extract only digits
        const digitsOnly = cleaned.replace(/\D/g, '');

        // If it's 10 digits, assume US number
        if (digitsOnly.length === 10) {
          cleaned = '+1' + digitsOnly;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
          // If it's 11 digits starting with 1, assume US number with country code
          cleaned = '+' + digitsOnly;
        } else {
          // For other lengths, we can't assume the country code
          return { isValid: false, normalized: cleaned };
        }
      }

      // Find country code
      const countryCodes = [
        '+1',
        '+44',
        '+61',
        '+49',
        '+33',
        '+81',
        '+82',
        '+86',
        '+91',
        '+52',
        '+55',
        '+34',
        '+39',
        '+31',
        '+46',
        '+47',
        '+45',
        '+358',
        '+41',
      ];
      const countryCode = countryCodes
        .sort((a, b) => b.length - a.length)
        .find((code) => cleaned.startsWith(code));

      if (!countryCode) {
        return { isValid: false, normalized: cleaned };
      }

      const localNumber = cleaned.slice(countryCode.length);
      const numbersOnly = localNumber.replace(/\D/g, '');

      // US/Canada numbers should be 10 digits
      if (countryCode === '+1') {
        const isValid = numbersOnly.length === 10;
        return { isValid, normalized: cleaned };
      }

      // International numbers should be 6-15 digits
      const isValid = numbersOnly.length >= 6 && numbersOnly.length <= 15;
      return { isValid, normalized: cleaned };
    };

    // Comprehensive validation that collects all errors
    const errors: string[] = [];
    const emailSet = new Set();
    const requiredHeaders = ['unit name', 'email address', 'first name', 'last name'];

    // Validate each row
    jsonData.forEach((item, index) => {
      const rowNumber = index + 2; // +2 because index is 0-based and we skip header row

      // Check required headers
      requiredHeaders.forEach((header) => {
        if (!(header in item) || !item[header] || item[header].trim() === '') {
          errors.push(`Row ${rowNumber}: Missing required field "${header}"`);
        }
      });

      // Check email format and duplicates
      const email = item['email address'];
      if (email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          errors.push(`Row ${rowNumber}: Invalid email format "${email}"`);
        } else if (emailSet.has(email)) {
          errors.push(`Row ${rowNumber}: Duplicate email address "${email}"`);
        } else {
          emailSet.add(email);
        }
      }

      // Check phone number
      const phoneNumber = item['phone number'];
      if (phoneNumber && phoneNumber.trim() !== '') {
        const phoneResult = normalizeAndValidatePhoneNumber(phoneNumber);
        if (!phoneResult.isValid) {
          errors.push(`Row ${rowNumber}: Invalid phone number "${phoneNumber}"`);
        }
      }

      // Check unit name exists
      const unitName = item['unit name'];
      if (unitName) {
        const unit = units.find((u) => u.name === unitName);
        if (!unit) {
          errors.push(`Row ${rowNumber}: Unit "${unitName}" not found in your account`);
        }
      }
    });

    if (errors.length === 0) {
      const subscribers = jsonData.map((item: any) => {
        const unit = units.find((u) => u.name === item['unit name']);
        // Normalize and validate phone number
        const phoneResult = normalizeAndValidatePhoneNumber(item['phone number']);

        // Extract digits from normalized phone number for API
        let phoneDigits = '';
        if (phoneResult.normalized && phoneResult.isValid) {
          // Always send the full normalized phone number with country code
          phoneDigits = phoneResult.normalized;
        }

        return {
          email: item['email address'],
          first_name: item['first name'],
          last_name: item['last name'],
          phone_number: phoneDigits || undefined,
          unit_subscriptions: unit ? [{ unit_id: unit.id }] : [],
        };
      });

      const hasInvalidUnit = subscribers.some(
        (subscriber) => subscriber.unit_subscriptions.length === 0,
      );

      if (hasInvalidUnit) {
        setIsWrongData(true);
        setIsLoading(false);
        resetFileInput();
      } else {
        setIsWrongData(false);
        let data: ISubscibersBody = {
          subscribers: subscribers,
        };
        onSubmit(data, fileType)
          .then(() => {
            setIsLoading(false);
            onClose();
          })
          .catch((error) => {
            setIsLoading(false);
            resetFileInput();
            // Handle API errors
            if (error?.response?.data?.message) {
              setValidationErrors([`API Error: ${error.response.data.message}`]);
            } else if (error?.message) {
              setValidationErrors([`Error: ${error.message}`]);
            } else {
              setValidationErrors(['An unexpected error occurred while uploading']);
            }
            setIsWrongData(true);
          });
      }
    } else {
      setValidationErrors(errors);
      setIsWrongData(true);
      setIsLoading(false);
      resetFileInput();
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '.MuiOutlinedInput-root': {
            borderRadius: '14px !important',
            minHeight: '45px !important',
            padding: '0 !important',
          },
          '.MuiOutlinedInput-input': {
            padding: '8px 12px !important',
            lineHeight: '1.5em',
          },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 2px rgba(71,144,204,0.2) !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth={'sm'}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle
          id="form-dialog-title"
          sx={{ margin: 'auto', textAlign: 'center', paddingTop: 5 }}
        >
          <Stack direction={'column'}>
            <Box sx={{ fontSize: '42px', fontWeight: 'bold' }}>
              {isWrongData ? 'Error importing tenants' : 'Import new tenants'}
            </Box>
            <Box sx={{ fontSize: '18px' }}>
              {isWrongData
                ? 'We tried uploading your file, but received errors.'
                : 'Import a list of new tenants and assign them to a property'}
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey', marginTop: 2 }} />
        <DialogContent sx={{ margin: 3 }}>
          {!isWrongData && <RenderInfo />}
          {isWrongData && <RenderWarningInfo validationErrors={validationErrors} />}
        </DialogContent>
        <Divider variant="fullWidth" sx={{ borderWidth: 1, borderColor: 'grey' }} />
        <DialogActions sx={{ justifyContent: 'space-between !important', margin: 2 }}>
          <SWLoadingButtonOutlined onClick={handleClose}>Cancel</SWLoadingButtonOutlined>
          <>
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              onClick={handleButtonClick}
            >
              Upload Spreadsheet
            </LoadingButton>
            <input
              aria-label="upload spreadsheet"
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RenderInfo = () => {
  const handleTemplateDownload = (e: React.MouseEvent) => {
    e.preventDefault();

    // Fetch the CSV file and force download
    fetch('/tenants_template.csv')
      .then((response) => response.blob())
      .then((blob) => {
        // Create blob URL
        const url = window.URL.createObjectURL(blob);

        // Create temporary anchor element and trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tenants_template.csv';
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error('Failed to download template:', err);
        // Fallback to direct navigation if blob download fails
        window.location.href = '/tenants_template.csv';
      });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <span style={{ fontWeight: 'bold' }}>
          1.{' '}
          <a
            href="/tenants_template.csv"
            download="tenants_template.csv"
            onClick={handleTemplateDownload}
          >
            Download this template.
          </a>
        </span>{' '}
        This pre-formatted spreadsheet is designed to ensure your information is accurately captured
        and processed efficiently.
      </Box>
      <Box>
        <span style={{ fontWeight: 'bold' }}>2. Add your tenant's information.</span> Please fill
        out the spreadsheet with your data. Required fields are marked with an asterisk (*).
        <Box paddingLeft={2} paddingTop={1}>
          <ul style={{ margin: 0 }}>
            <li>Unit Name *</li>
            <li>Email Address *</li>
            <li>Phone Number (optional)</li>
            <li>First Name *</li>
            <li>Last Name *</li>
          </ul>
        </Box>
      </Box>
      <Box>
        <span style={{ fontWeight: 'bold' }}>3. Save and Upload.</span> Once you have filled out the
        spreadsheet with your information, save the file as CSV or XLSX format.
        <Box paddingLeft={2} paddingTop={1} sx={{ fontSize: '0.9em', color: 'text.secondary' }}>
          <strong>Mac Numbers users:</strong> Go to File → Export To → CSV or Excel to save in a
          compatible format.
        </Box>
        Return to this page and click on the "Upload Spreadsheet" button. Select your file and
        submit it for processing.
      </Box>
    </Stack>
  );
};

const RenderWarningInfo = ({ validationErrors }: { validationErrors: string[] }) => (
  <Stack spacing={3}>
    {validationErrors.length > 0 ? (
      <Box>
        <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>
          Found {validationErrors.length} error{validationErrors.length > 1 ? 's' : ''} in your
          file:
        </Box>
        <Box
          sx={{
            maxHeight: 300,
            overflowY: 'auto',
            backgroundColor: '#f5f5f5',
            padding: 2,
            borderRadius: 1,
            border: '1px solid #ddd',
          }}
        >
          {validationErrors.map((error, index) => (
            <Box key={index} sx={{ marginBottom: 0.5, fontSize: '14px', color: '#d32f2f' }}>
              • {error}
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      <Box>
        <Box>Please check the data in your spreadsheet. Here's a few common issues...</Box>
        <Box paddingLeft={2} paddingTop={1}>
          <ol style={{ margin: 0 }}>
            <li>
              Make sure your unit name is in the spreadsheet match your unit names in the web
              application
            </li>
            <li>Double check there are valid email addresses in the email address field</li>
            <li>
              Ensure phone numbers are valid (accepts formats like +1234567890, 1234567890, (123)
              456-7890, 123 456 7890)
            </li>
            <li>Make sure the file is a CSV or XLSX.</li>
          </ol>
        </Box>
      </Box>
    )}
    <Box>Still need help? Contact support@simplesubwater.com</Box>
  </Stack>
);

export default FormBulkSubscriber;
